const SKIP_LINKIFY_TAGS = new Set(['A', 'CODE', 'PRE', 'SCRIPT', 'STYLE', 'TEXTAREA', 'VIDEO', 'SOURCE']);

/** http(s) и www — для поиска URL в тексте */
const URL_IN_TEXT_RE = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

export const normalizeUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) {
        return '';
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    if (/^www\./i.test(trimmed)) {
        return `https://${trimmed}`;
    }
    return `https://${trimmed}`;
};

export const canonicalLink = (raw: string): string => {
    try {
        const parsed = new URL(normalizeUrl(raw));
        parsed.hash = '';
        parsed.hostname = parsed.hostname.toLowerCase();
        let href = parsed.href;
        if (href.endsWith('/') && parsed.pathname === '/') {
            href = href.slice(0, -1);
        }
        return href.toLowerCase();
    } catch {
        return raw.trim().toLowerCase();
    }
};

export const isExternalHttpUrl = (raw: string): boolean => {
    try {
        const parsed = new URL(normalizeUrl(raw));
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return false;
        }
        if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
            return false;
        }
        if (!parsed.hostname.includes('.')) {
            return false;
        }
        return Boolean(parsed.hostname);
    } catch {
        return false;
    }
};

export const getFaviconUrl = (raw: string): string => {
    try {
        const { hostname } = new URL(normalizeUrl(raw));
        return `https://icons.duckduckgo.com/ip3/${hostname}.ico`;
    } catch {
        return '';
    }
};

const trimUrlMatch = (raw: string): { url: string; trail: string } => {
    let url = raw;
    let trail = '';

    while (url && /[.,;:!?]+$/.test(url)) {
        trail = `${url.slice(-1)}${trail}`;
        url = url.slice(0, -1);
    }

    while (
        url.endsWith(')')
        && (url.match(/\(/g)?.length || 0) < (url.match(/\)/g)?.length || 0)
    ) {
        trail = `)${trail}`;
        url = url.slice(0, -1);
    }

    while (
        url.endsWith(']')
        && (url.match(/\[/g)?.length || 0) < (url.match(/]/g)?.length || 0)
    ) {
        trail = `]${trail}`;
        url = url.slice(0, -1);
    }

    return { url, trail };
};

const parseRoot = (html: string): { doc: Document; root: HTMLElement } | null => {
    if (!html || typeof DOMParser === 'undefined') {
        return null;
    }
    const doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
    const root = doc.getElementById('__root');
    if (!root) {
        return null;
    }
    return { doc, root };
};

const appendFavicon = (doc: Document, anchor: HTMLAnchorElement, href: string) => {
    if (anchor.querySelector('.task-content-link-favicon')) {
        return;
    }
    const favicon = getFaviconUrl(href);
    if (!favicon) {
        return;
    }
    const img = doc.createElement('img');
    img.className = 'task-content-link-favicon';
    img.src = favicon;
    img.alt = '';
    img.draggable = false;
    anchor.insertBefore(img, anchor.firstChild);
};

const formatAnchor = (doc: Document, anchor: HTMLAnchorElement, withFavicon: boolean) => {
    const href = anchor.getAttribute('href') || '';
    if (!isExternalHttpUrl(href)) {
        return;
    }
    const normalized = normalizeUrl(href);
    anchor.setAttribute('href', normalized);
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
    anchor.classList.add('task-content-link');
    if (withFavicon) {
        appendFavicon(doc, anchor, normalized);
    }
};

const collectTextNodes = (root: HTMLElement): Text[] => {
    const nodes: Text[] = [];
    const walk = (node: Node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (SKIP_LINKIFY_TAGS.has(el.tagName)) {
                return;
            }
            if (el.classList?.contains('task-content-link-favicon')) {
                return;
            }
            Array.from(node.childNodes).forEach(walk);
            return;
        }
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue) {
            nodes.push(node as Text);
        }
    };
    walk(root);
    return nodes;
};

const linkifyTextNode = (doc: Document, textNode: Text, withFavicon: boolean) => {
    const text = textNode.nodeValue || '';
    URL_IN_TEXT_RE.lastIndex = 0;
    if (!URL_IN_TEXT_RE.test(text)) {
        return;
    }

    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    URL_IN_TEXT_RE.lastIndex = 0;
    let match: RegExpExecArray | null = URL_IN_TEXT_RE.exec(text);

    while (match) {
        const { url, trail } = trimUrlMatch(match[0]);
        if (match.index > lastIndex) {
            fragment.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
        }

        if (isExternalHttpUrl(url)) {
            const anchor = doc.createElement('a');
            const href = normalizeUrl(url);
            anchor.setAttribute('href', href);
            anchor.setAttribute('target', '_blank');
            anchor.setAttribute('rel', 'noopener noreferrer');
            anchor.className = 'task-content-link';
            anchor.textContent = url;
            if (withFavicon) {
                appendFavicon(doc, anchor, href);
            }
            fragment.appendChild(anchor);
            if (trail) {
                fragment.appendChild(doc.createTextNode(trail));
            }
        } else {
            fragment.appendChild(doc.createTextNode(match[0]));
        }

        lastIndex = match.index + match[0].length;
        match = URL_IN_TEXT_RE.exec(text);
    }

    if (lastIndex < text.length) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
};

const transformHtmlLinks = (html: string, withFavicon: boolean): string => {
    const parsed = parseRoot(html);
    if (!parsed) {
        return html;
    }
    const { doc, root } = parsed;
    root.querySelectorAll('a[href]').forEach((node) => {
        formatAnchor(doc, node as HTMLAnchorElement, withFavicon);
    });
    collectTextNodes(root).forEach((node) => linkifyTextNode(doc, node, withFavicon));
    return root.innerHTML;
};

/** Оборачивает голые URL в <a>, без favicon — для сохранения в редакторе */
export const linkifyHtml = (html: string): string => transformHtmlLinks(html, false);

/** Кликабельные ссылки + favicon справа — только для просмотра */
export const decorateContentLinks = (html: string): string => transformHtmlLinks(html, true);

export const decoratePlainTextLinks = (text: string): string => {
    if (!text) {
        return '';
    }
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    return decorateContentLinks(escaped);
};

const pushUniqueUrl = (urls: string[], raw: string) => {
    if (!isExternalHttpUrl(raw) || raw.length > 500) {
        return;
    }
    const url = normalizeUrl(raw);
    const key = canonicalLink(url);
    if (urls.some((item) => canonicalLink(item) === key)) {
        return;
    }
    urls.push(url);
};

export const extractUrlsFromText = (text: string): string[] => {
    const urls: string[] = [];
    if (!text) {
        return urls;
    }
    URL_IN_TEXT_RE.lastIndex = 0;
    let match: RegExpExecArray | null = URL_IN_TEXT_RE.exec(text);
    while (match) {
        pushUniqueUrl(urls, trimUrlMatch(match[0]).url);
        match = URL_IN_TEXT_RE.exec(text);
    }
    return urls;
};

export const extractUrlsFromHtml = (html: string): string[] => {
    const urls: string[] = [];
    const parsed = parseRoot(html);
    if (!parsed) {
        return extractUrlsFromText(html.replace(/<[^>]+>/g, ' '));
    }
    const { root } = parsed;

    root.querySelectorAll('a[href]').forEach((node) => {
        const href = (node as HTMLAnchorElement).getAttribute('href') || '';
        pushUniqueUrl(urls, href);
    });

    collectTextNodes(root).forEach((node) => {
        extractUrlsFromText(node.nodeValue || '').forEach((url) => pushUniqueUrl(urls, url));
    });

    return urls;
};

export const extractTaskUrls = (task: {
    taskName?: string | null;
    taskContent?: string | null;
}): string[] => {
    const urls: string[] = [];
    extractUrlsFromText(task.taskName || '').forEach((url) => pushUniqueUrl(urls, url));
    extractUrlsFromHtml(task.taskContent || '').forEach((url) => pushUniqueUrl(urls, url));
    return urls;
};

export type DisplayTaskLink = {
    key: string;
    url: string;
    linkId?: number;
    fromContent: boolean;
};

export const mergeTaskLinks = (
    stored: Array<{ linkId: number; linkPath: string }> | null | undefined,
    contentUrls: string[],
): DisplayTaskLink[] => {
    const result: DisplayTaskLink[] = [];
    const seen = new Set<string>();

    (stored || []).forEach((link) => {
        const url = normalizeUrl(link.linkPath);
        const key = canonicalLink(url);
        if (!url || seen.has(key)) {
            return;
        }
        seen.add(key);
        result.push({
            key,
            url,
            linkId: link.linkId,
            fromContent: false,
        });
    });

    contentUrls.forEach((raw) => {
        const url = normalizeUrl(raw);
        const key = canonicalLink(url);
        if (!url) {
            return;
        }
        if (seen.has(key)) {
            const existing = result.find((item) => item.key === key);
            if (existing) {
                existing.fromContent = true;
            }
            return;
        }
        seen.add(key);
        result.push({ key, url, fromContent: true });
    });

    return result;
};

export const uniqueNormalizedUrls = (...groups: string[][]): string[] => {
    const urls: string[] = [];
    groups.forEach((group) => {
        group.forEach((raw) => pushUniqueUrl(urls, raw));
    });
    return urls;
};

export const linksNotIn = (candidates: string[], existing: string[]): string[] => {
    const seen = new Set(existing.map((item) => canonicalLink(item)).filter(Boolean));
    const urls: string[] = [];
    candidates.forEach((raw) => {
        if (!isExternalHttpUrl(raw) || raw.length > 500) {
            return;
        }
        const url = normalizeUrl(raw);
        const key = canonicalLink(url);
        if (!url || seen.has(key)) {
            return;
        }
        seen.add(key);
        urls.push(url);
    });
    return urls;
};
